import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BrandMark from "../components/BrandMark";
import { api } from "../lib/api";

const S = {
  it: {
    overline: "Promemoria KYC",
    title: "Preferenze",
    title_em: "email",
    loading: "Verifica del link…",
    invalid_title: "Link non valido",
    invalid_msg: "Questo link di disiscrizione non è valido o è scaduto. Puoi ignorare questo messaggio.",
    confirm_heading: "Annulla l'iscrizione ai promemoria",
    confirm_msg: "Confermando, {email} non riceverà più i promemoria periodici del team Compliance sui documenti KYC mancanti. Le comunicazioni ufficiali (estratti, conferme) continueranno ad arrivare.",
    confirm_btn: "Conferma disiscrizione",
    working: "Elaborazione…",
    done_title: "Disiscrizione confermata",
    done_msg: "Non invieremo più promemoria KYC a {email}. Se cambi idea, contatta compliance@borealisreserve.com.",
    back: "Vai al portale",
  },
  en: {
    overline: "KYC reminders",
    title: "Email",
    title_em: "preferences",
    loading: "Verifying the link…",
    invalid_title: "Invalid link",
    invalid_msg: "This unsubscribe link is invalid or has expired. You can ignore this message.",
    confirm_heading: "Unsubscribe from reminders",
    confirm_msg: "By confirming, {email} will no longer receive the Compliance team's periodic reminders about missing KYC documents. Official communications (statements, confirmations) will continue.",
    confirm_btn: "Confirm unsubscribe",
    working: "Processing…",
    done_title: "Unsubscribe confirmed",
    done_msg: "We will no longer send KYC reminders to {email}. If you change your mind, contact compliance@borealisreserve.com.",
    back: "Go to the portal",
  },
  no: {
    overline: "KYC-påminnelser",
    title: "E-post",
    title_em: "innstillinger",
    loading: "Verifiserer lenken…",
    invalid_title: "Ugyldig lenke",
    invalid_msg: "Denne avmeldingslenken er ugyldig eller utløpt. Du kan se bort fra denne meldingen.",
    confirm_heading: "Meld deg av påminnelser",
    confirm_msg: "Ved å bekrefte vil {email} ikke lenger motta Compliance-teamets periodiske påminnelser om manglende KYC-dokumenter. Offisiell kommunikasjon (kontoutskrifter, bekreftelser) fortsetter.",
    confirm_btn: "Bekreft avmelding",
    working: "Behandler…",
    done_title: "Avmelding bekreftet",
    done_msg: "Vi sender ikke lenger KYC-påminnelser til {email}. Hvis du ombestemmer deg, kontakt compliance@borealisreserve.com.",
    back: "Gå til portalen",
  },
  de: {
    overline: "KYC-Erinnerungen",
    title: "E-Mail-",
    title_em: "Einstellungen",
    loading: "Link wird überprüft…",
    invalid_title: "Ungültiger Link",
    invalid_msg: "Dieser Abmeldelink ist ungültig oder abgelaufen. Sie können diese Nachricht ignorieren.",
    confirm_heading: "Erinnerungen abbestellen",
    confirm_msg: "Mit der Bestätigung erhält {email} keine periodischen Erinnerungen des Compliance-Teams zu fehlenden KYC-Dokumenten mehr. Offizielle Mitteilungen (Kontoauszüge, Bestätigungen) werden weiterhin gesendet.",
    confirm_btn: "Abmeldung bestätigen",
    working: "Wird verarbeitet…",
    done_title: "Abmeldung bestätigt",
    done_msg: "Wir senden keine KYC-Erinnerungen mehr an {email}. Falls Sie es sich anders überlegen, kontaktieren Sie compliance@borealisreserve.com.",
    back: "Zum Portal",
  },
};

export default function UnsubscribeRemindersPage() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ready | invalid | done
  const [email, setEmail] = useState("");
  const [loc, setLoc] = useState("it");
  const [busy, setBusy] = useState(false);
  const t = S[loc] || S.it;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/kyc-reminders/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setEmail(data.email || "");
        if (data.locale && S[data.locale]) setLoc(data.locale);
        setStatus(data.unsubscribed ? "done" : "ready");
      } catch {
        if (!cancelled) setStatus("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const unsubscribe = async () => {
    setBusy(true);
    try {
      await api.post(`/public/kyc-reminders/${encodeURIComponent(token)}/unsubscribe`);
      setStatus("done");
    } catch {
      setStatus("invalid");
    } finally {
      setBusy(false);
    }
  };

  const withEmail = (s) => s.replace("{email}", email || "—");

  return (
    <div className="min-h-screen br-aurora flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-[480px] w-full br-card px-8 py-10 br-fade-up" data-testid="unsubscribe-card">
        <div className="flex items-center gap-4 mb-6">
          <BrandMark size={44} />
          <div>
            <div className="br-overline">{t.overline}</div>
            <div className="font-serif-display italic text-[1.2rem] text-[color:var(--br-parchment)] mt-1 leading-none">
              {t.title} <span className="text-[color:var(--br-bronze-soft)]">{t.title_em}</span>
            </div>
          </div>
        </div>

        {status === "loading" && (
          <p className="text-[color:var(--br-text-dim)] text-[0.9rem]" data-testid="unsubscribe-loading">
            {t.loading}
          </p>
        )}

        {status === "invalid" && (
          <div data-testid="unsubscribe-invalid">
            <h2 className="font-serif-display text-[1.5rem] leading-tight">{t.invalid_title}</h2>
            <p className="mt-4 text-[0.92rem] text-[color:var(--br-text)] leading-relaxed">{t.invalid_msg}</p>
            <Link to="/login" className="br-btn-outline mt-6 inline-block" data-testid="unsubscribe-back">
              {t.back}
            </Link>
          </div>
        )}

        {status === "ready" && (
          <div data-testid="unsubscribe-ready">
            <h2 className="font-serif-display text-[1.55rem] leading-tight">{t.confirm_heading}</h2>
            <p className="mt-4 text-[0.92rem] text-[color:var(--br-text)] leading-relaxed">
              {withEmail(t.confirm_msg)}
            </p>
            <button
              type="button"
              onClick={unsubscribe}
              disabled={busy}
              className="br-btn-primary mt-6"
              data-testid="unsubscribe-confirm-btn"
            >
              {busy ? t.working : t.confirm_btn}
            </button>
          </div>
        )}

        {status === "done" && (
          <div data-testid="unsubscribe-done">
            <h2 className="font-serif-display text-[1.55rem] leading-tight">{t.done_title}</h2>
            <p className="mt-4 text-[0.92rem] text-[color:var(--br-text)] leading-relaxed">
              {withEmail(t.done_msg)}
            </p>
            <Link to="/login" className="br-btn-outline mt-6 inline-block" data-testid="unsubscribe-back">
              {t.back}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
